import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  LayoutDashboard,
  FlaskConical,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Activity,
  Users,
  Server,
  BookOpen,
  User,
  UserCog,
  GraduationCap,
  Calendar,
  FolderOpen,
  Folder,
  Lock,
  Sun,
  Moon
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { studentPortal } from '../services/api';
import VersionFooter from './VersionFooter';
import useEnvironmentStats from '../hooks/useEnvironmentStats';
import { connectLiveUpdates } from '../services/liveUpdates';
import { countLabel, statsTreeLabs, sumRunning } from '../utils/environmentStats';
import EnvironmentStatsStatus, { AdditionalEnvironmentStates } from './EnvironmentStatsStatus';
import { labLabel, compareLabLabels } from '../utils/labNaming';

// Helper to format term code to display name
function formatTermDisplay(term) {
  if (!term || term.length !== 4) return term || 'Unknown';
  const year = term.substring(1, 3);
  const termCode = term.substring(3);
  const termNames = { '1': 'Winter', '5': 'Spring', '8': 'Fall' };
  const termName = termNames[termCode] || 'Term';
  return `${termName} ${year}`;
}

// Helper to get short term prefix (e.g., "W26", "F25")
function getTermPrefix(term) {
  if (!term || term.length !== 4) return '';
  const year = term.substring(1, 3);
  const termCode = term.substring(3);
  const prefixes = { '1': 'W', '5': 'Sp', '8': 'F' };
  return `${prefixes[termCode] || 'T'}${year}`;
}

// Group labs into sorted sections with optional stats
function groupLabsBySection(labs, { withStats = false } = {}) {
  const sectionMap = new Map();
  labs.forEach(lab => {
    const section = lab.section || null;
    if (!sectionMap.has(section)) {
      sectionMap.set(section, []);
    }
    sectionMap.get(section).push(lab);
  });

  return Array.from(sectionMap.entries())
    .sort((a, b) => {
      if (a[0] === null) return 1;
      if (b[0] === null) return -1;
      return (parseInt(a[0]) || 0) - (parseInt(b[0]) || 0);
    })
    .map(([section, sectionLabs]) => {
      sectionLabs.sort(compareLabLabels);
      const node = {
        section,
        displayName: section ? `S${section}` : 'Unsectioned',
        labs: sectionLabs
      };
      if (withStats) {
        node.totalRunning = sumRunning(sectionLabs);
      }
      return node;
    });
}

function Layout({ children, userRole }) {
  const { user, logout, isAdmin, isStudent, isFaculty } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 768);
  const queryClient = useQueryClient();
  const [liveConnected, setLiveConnected] = useState(false);
  const [expandedTerms, setExpandedTerms] = useState(new Set());
  const [expandedCourses, setExpandedCourses] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState(new Set());

  // Leave room for the workspace on phones, including when rotating/resizing
  // an already-open page. Navigation returns a manually expanded rail to its
  // compact state once a destination has been selected.
  useEffect(() => {
    const compact = () => { if (window.innerWidth < 768) setCollapsed(true); };
    compact();
    window.addEventListener('resize', compact);
    return () => window.removeEventListener('resize', compact);
  }, [location.pathname]);

  // Fetch initial stats (only for faculty/admin)
  const statsQuery = useEnvironmentStats(!isStudent);
  const stats = statsQuery.data;

  // Fetch student labs for tree navigation
  const { data: studentLabsData } = useQuery({
    queryKey: ['studentLabs'],
    queryFn: () => studentPortal.getLabs(),
    enabled: isStudent,
    refetchInterval: 60000,
  });

  // Lab counts and running counts in the tree come from the SAME observation
  // as the footer, including ownership scope, draft labs and closed labs.
  const adminLabsData = useMemo(() => statsTreeLabs(stats), [stats]);

  // Build hierarchical tree structure: Term -> Course -> Labs
  const labsTree = useMemo(() => {
    if (!studentLabsData?.labs) return [];
    
    const labs = studentLabsData.labs;
    const termMap = new Map();
    
    labs.forEach(lab => {
      const term = lab.term || 'unknown';
      const courseCode = lab.courseCode || 'Unknown';
      
      if (!termMap.has(term)) {
        termMap.set(term, new Map());
      }
      
      const courseMap = termMap.get(term);
      if (!courseMap.has(courseCode)) {
        courseMap.set(courseCode, []);
      }
      
      courseMap.get(courseCode).push(lab);
    });
    
    // Convert to array and sort
    const tree = [];
    const sortedTerms = Array.from(termMap.keys()).sort((a, b) => b.localeCompare(a)); // Newest first
    
    sortedTerms.forEach(term => {
      const courses = [];
      const courseMap = termMap.get(term);
      const sortedCourses = Array.from(courseMap.keys()).sort();
      
      sortedCourses.forEach(courseCode => {
        const sections = groupLabsBySection(courseMap.get(courseCode));
        courses.push({
          courseCode,
          sections,
          labCount: sections.reduce((sum, s) => sum + s.labs.length, 0)
        });
      });

      tree.push({
        term,
        termDisplay: formatTermDisplay(term),
        termPrefix: getTermPrefix(term),
        courses,
        labCount: courses.reduce((sum, c) => sum + c.labCount, 0)
      });
    });
    
    return tree;
  }, [studentLabsData]);

  // Build hierarchical tree structure for admin/faculty: Term -> Course -> Labs
  const adminLabsTree = useMemo(() => {
    if (!adminLabsData) return [];
    
    const labsList = adminLabsData;
    const termMap = new Map();
    
    labsList.forEach(lab => {
      const term = lab.term || 'unknown';
      const courseCode = lab.courseCode || 'Unknown';
      
      if (!termMap.has(term)) {
        termMap.set(term, new Map());
      }
      
      const courseMap = termMap.get(term);
      if (!courseMap.has(courseCode)) {
        courseMap.set(courseCode, []);
      }
      
      courseMap.get(courseCode).push(lab);
    });
    
    // Convert to array and sort
    const tree = [];
    const sortedTerms = Array.from(termMap.keys()).sort((a, b) => b.localeCompare(a)); // Newest first
    
    sortedTerms.forEach(term => {
      const courses = [];
      const courseMap = termMap.get(term);
      const sortedCourses = Array.from(courseMap.keys()).sort();
      
      sortedCourses.forEach(courseCode => {
        const courseLabsList = courseMap.get(courseCode);
        const sections = groupLabsBySection(courseLabsList, { withStats: true });

        const totalRunning = sumRunning(courseLabsList);

        courses.push({
          courseCode,
          sections,
          labCount: sections.reduce((sum, s) => sum + s.labs.length, 0),
          totalRunning
        });
      });

      tree.push({
        term,
        termDisplay: formatTermDisplay(term),
        termPrefix: getTermPrefix(term),
        courses,
        labCount: courses.reduce((sum, c) => sum + c.labCount, 0)
      });
    });
    
    return tree;
  }, [adminLabsData]);

  // Auto-expand current lab's term, course, and section
  useEffect(() => {
    const tree = isStudent ? labsTree : adminLabsTree;
    if (!tree.length) return;

    const labIdMatch = location.pathname.match(/\/labs\/([^/]+)/);
    if (labIdMatch) {
      const currentLabId = labIdMatch[1];
      tree.forEach(termNode => {
        termNode.courses.forEach(courseNode => {
          courseNode.sections.forEach(sectionNode => {
            const hasLab = sectionNode.labs.some(lab => lab.id === currentLabId);
            if (hasLab) {
              setExpandedTerms(prev => new Set([...prev, termNode.term]));
              setExpandedCourses(prev => new Set([...prev, `${termNode.term}-${courseNode.courseCode}`]));
              setExpandedSections(prev => new Set([...prev, `${termNode.term}-${courseNode.courseCode}-${sectionNode.section}`]));
            }
          });
        });
      });
    }
  }, [location.pathname, labsTree, adminLabsTree, isStudent]);

  const toggleTerm = (term) => {
    setExpandedTerms(prev => {
      const next = new Set(prev);
      if (next.has(term)) {
        next.delete(term);
      } else {
        next.add(term);
      }
      return next;
    });
  };

  const toggleCourse = (termCourseKey, courseNode) => {
    const isExpanding = !expandedCourses.has(termCourseKey);
    setExpandedCourses(prev => {
      const next = new Set(prev);
      if (next.has(termCourseKey)) {
        next.delete(termCourseKey);
      } else {
        next.add(termCourseKey);
      }
      return next;
    });
    // Auto-expand if course has only one section
    if (isExpanding && courseNode?.sections?.length === 1) {
      const sectionKey = `${termCourseKey}-${courseNode.sections[0].section}`;
      setExpandedSections(prev => new Set([...prev, sectionKey]));
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  // WebSocket connection for live updates (only for faculty/admin)
  useEffect(() => {
    if (isStudent) return; // Students don't need live stats
    
    const newSocket = connectLiveUpdates();
    
    newSocket.on('connect', () => {
      setLiveConnected(true);
      queryClient.invalidateQueries({ queryKey: ['containerStats'] });
    });
    
    newSocket.on('containers:update', () => {
      queryClient.invalidateQueries({ queryKey: ['containerStats'] });
    });
    const disconnected = () => {
      setLiveConnected(false);
      queryClient.invalidateQueries({ queryKey: ['containerStats'] });
    };
    newSocket.on('disconnect', disconnected);
    newSocket.on('connect_error', disconnected);
    
    return () => newSocket.close();
  }, [isStudent, user?.id, user?._id, queryClient]);

  const handleLogout = () => {
    logout();
    // Redirect students to student login, others to admin login
    navigate(isStudent ? '/student-login' : '/login');
  };

  const displayStats = stats;

  // Determine sidebar title based on role
  const sidebarTitle = isStudent ? 'Student Portal' : 'Lab Admin';

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Skip to main content link for keyboard navigation */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          zIndex: 10000,
        }}
        onFocus={(e) => {
          e.currentTarget.style.position = 'fixed';
          e.currentTarget.style.top = '8px';
          e.currentTarget.style.left = '8px';
          e.currentTarget.style.width = 'auto';
          e.currentTarget.style.height = 'auto';
          e.currentTarget.style.padding = '12px 24px';
          e.currentTarget.style.background = 'var(--accent-primary)';
          e.currentTarget.style.color = 'white';
          e.currentTarget.style.borderRadius = '8px';
          e.currentTarget.style.fontSize = '14px';
          e.currentTarget.style.fontWeight = '600';
          e.currentTarget.style.textDecoration = 'none';
          e.currentTarget.style.overflow = 'visible';
        }}
        onBlur={(e) => {
          e.currentTarget.style.position = 'absolute';
          e.currentTarget.style.left = '-9999px';
          e.currentTarget.style.width = '1px';
          e.currentTarget.style.height = '1px';
          e.currentTarget.style.overflow = 'hidden';
        }}
      >
        Skip to main content
      </a>

      {/* Sidebar */}
      <aside
        role="complementary"
        aria-label={isStudent ? 'Student navigation sidebar' : 'Admin navigation sidebar'}
        style={{
        width: collapsed ? 64 : 240,
        flexShrink: 0,
        background: 'var(--bg-secondary)',
        borderRight: '1px solid var(--border-primary)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        // Pinned to the viewport. Left to stretch, the aside grows to the full
        // document height, so on a long Labs list Live Status and the logout
        // button end up at the bottom of a page-tall column — out of reach
        // without scrolling to the end of the page. Sticky (not fixed) keeps it
        // in normal flow, so the flex row still reserves its width for free and
        // the collapse transition between 64px and 240px keeps working.
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        height: '100vh',
      }}>
        {/* Logo / Header */}
        <header style={{
          padding: '20px 16px',
          borderBottom: '1px solid var(--border-primary)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <div
            style={{
              width: 32,
              height: 32,
              background: isStudent
                ? 'linear-gradient(135deg, var(--accent-success), var(--accent-primary))'
                : 'linear-gradient(135deg, var(--accent-primary), var(--accent-purple))',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-hidden="true"
          >
            {isStudent ? <GraduationCap size={18} color="white" /> : <Server size={18} color="white" />}
          </div>
          {!collapsed && (
            <span style={{ fontWeight: 600, fontSize: 16 }}>{sidebarTitle}</span>
          )}
        </header>

        {/* Navigation - Different for students vs faculty */}
        <nav
          role="navigation"
          aria-label="Main navigation"
          style={{ flex: 1, minHeight: 0, padding: '16px 8px', overflowY: 'auto' }}
        >
          {isStudent ? (
            // Student Navigation with Tree
            <>
              <NavItem to="/" icon={BookOpen} collapsed={collapsed}>
                Dashboard
              </NavItem>
              
              {!collapsed && labsTree.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ 
                    fontSize: 11, 
                    color: 'var(--text-muted)', 
                    padding: '8px 12px 4px',
                    fontWeight: 600,
                    letterSpacing: '0.5px'
                  }}>
                    MY LABS
                  </div>
                  
                  {labsTree.map(termNode => (
                    <div key={termNode.term}>
                      {/* Term Level */}
                      <button
                        onClick={() => toggleTerm(termNode.term)}
                        aria-expanded={expandedTerms.has(termNode.term)}
                        aria-label={`${termNode.termDisplay} term, ${termNode.labCount} labs`}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 12px',
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-secondary)',
                          cursor: 'pointer',
                          borderRadius: 6,
                          fontSize: 13,
                          fontWeight: 500,
                          textAlign: 'left',
                        }}
                      >
                        {expandedTerms.has(termNode.term) ? (
                          <ChevronDown size={14} aria-hidden="true" />
                        ) : (
                          <ChevronRight size={14} aria-hidden="true" />
                        )}
                        <Calendar size={14} style={{ color: 'var(--accent-primary)' }} aria-hidden="true" />
                        <span>{termNode.termDisplay}</span>
                      </button>

                      {/* Courses under Term */}
                      {expandedTerms.has(termNode.term) && (
                        <div style={{ marginLeft: 12 }} role="group" aria-label={`Courses in ${termNode.termDisplay}`}>
                          {termNode.courses.map(courseNode => {
                            const termCourseKey = `${termNode.term}-${courseNode.courseCode}`;
                            return (
                              <div key={termCourseKey}>
                                {/* Course Level */}
                                <button
                                  onClick={() => toggleCourse(termCourseKey, courseNode)}
                                  aria-expanded={expandedCourses.has(termCourseKey)}
                                  aria-label={`${courseNode.courseCode}, ${courseNode.labCount} labs`}
                                  style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '6px 12px',
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    borderRadius: 6,
                                    fontSize: 12,
                                    textAlign: 'left',
                                  }}
                                >
                                  {expandedCourses.has(termCourseKey) ? (
                                    <FolderOpen size={13} style={{ color: 'var(--accent-warning)' }} aria-hidden="true" />
                                  ) : (
                                    <Folder size={13} style={{ color: 'var(--accent-warning)' }} aria-hidden="true" />
                                  )}
                                  <span style={{ fontWeight: 500 }}>{courseNode.courseCode}</span>
                                  <span style={{
                                    fontSize: 10,
                                    color: 'var(--text-muted)',
                                    marginLeft: 'auto'
                                  }} aria-hidden="true">
                                    {courseNode.labCount}
                                  </span>
                                </button>

                                {/* Sections under Course */}
                                {expandedCourses.has(termCourseKey) && (
                                  <div style={{ marginLeft: 16 }} role="group" aria-label={`Sections in ${courseNode.courseCode}`}>
                                    {courseNode.sections.map(sectionNode => {
                                      const sectionKey = `${termNode.term}-${courseNode.courseCode}-${sectionNode.section}`;
                                      return (
                                        <div key={sectionKey}>
                                          {/* Section Level */}
                                          <button
                                            onClick={() => toggleSection(sectionKey)}
                                            aria-expanded={expandedSections.has(sectionKey)}
                                            aria-label={`Section ${sectionNode.displayName}, ${sectionNode.labs.length} labs`}
                                            style={{
                                              width: '100%',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 8,
                                              padding: '5px 12px',
                                              background: 'none',
                                              border: 'none',
                                              color: 'var(--text-secondary)',
                                              cursor: 'pointer',
                                              borderRadius: 6,
                                              fontSize: 12,
                                              textAlign: 'left',
                                            }}
                                          >
                                            {expandedSections.has(sectionKey) ? (
                                              <ChevronDown size={12} aria-hidden="true" />
                                            ) : (
                                              <ChevronRight size={12} aria-hidden="true" />
                                            )}
                                            <Users size={12} style={{ color: 'var(--accent-primary)' }} aria-hidden="true" />
                                            <span style={{ fontWeight: 500 }}>{sectionNode.displayName}</span>
                                            <span style={{
                                              fontSize: 10,
                                              color: 'var(--text-muted)',
                                              marginLeft: 'auto'
                                            }} aria-hidden="true">
                                              {sectionNode.labs.length}
                                            </span>
                                          </button>

                                          {/* Labs under Section */}
                                          {expandedSections.has(sectionKey) && (
                                            <div style={{ marginLeft: 16 }} role="list" aria-label={`Labs in ${sectionNode.displayName}`}>
                                              {sectionNode.labs.map(lab => {
                                                const isActive = location.pathname === `/labs/${lab.id}`;
                                                const isLocked = lab.isD2LLocked;

                                                return (
                                                  <NavLink
                                                    key={lab.id}
                                                    to={`/labs/${lab.id}`}
                                                    role="listitem"
                                                    aria-label={`${labLabel(lab)}${isLocked ? ', requires D2L access' : ''}${lab.isAccessible && !isLocked ? ', accessible' : ''}`}
                                                    title={lab.name}
                                                    aria-current={isActive ? 'page' : undefined}
                                                    style={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 6,
                                                      padding: '5px 10px',
                                                      borderRadius: 4,
                                                      fontSize: 12,
                                                      color: isLocked
                                                        ? 'var(--accent-warning)'
                                                        : isActive
                                                          ? 'var(--text-primary)'
                                                          : 'var(--text-secondary)',
                                                      background: isActive ? 'var(--bg-tertiary)' : 'transparent',
                                                      textDecoration: 'none',
                                                      opacity: isLocked ? 0.7 : 1,
                                                    }}
                                                  >
                                                    {isLocked ? (
                                                      <Lock size={12} aria-hidden="true" />
                                                    ) : (
                                                      <FlaskConical size={12} style={{ color: 'var(--accent-purple)' }} aria-hidden="true" />
                                                    )}
                                                    <span style={{
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      whiteSpace: 'nowrap',
                                                      flex: 1
                                                    }}>
                                                      {labLabel(lab)}
                                                    </span>
                                                    {lab.isAccessible && !isLocked && (
                                                      <span
                                                        style={{
                                                          width: 6,
                                                          height: 6,
                                                          borderRadius: '50%',
                                                          background: 'var(--accent-success)',
                                                        }}
                                                        aria-hidden="true"
                                                      />
                                                    )}
                                                  </NavLink>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            // Faculty/Admin Navigation
            <>
              <NavItem to="/" icon={LayoutDashboard} collapsed={collapsed}>
                Dashboard
              </NavItem>
              <NavItem to="/labs" icon={FlaskConical} collapsed={collapsed}>
                Labs
              </NavItem>
              <NavItem to="/sections" icon={Users} collapsed={collapsed}>
                Sections
              </NavItem>
              <NavItem to="/analytics" icon={BarChart3} collapsed={collapsed}>
                Analytics
              </NavItem>
              {isAdmin && (
                <NavItem to="/users" icon={UserCog} collapsed={collapsed}>
                  Users
                </NavItem>
              )}
              {isAdmin && (
                <NavItem to="/settings" icon={Settings} collapsed={collapsed}>
                  Settings
                </NavItem>
              )}
              
              {/* Admin Labs Tree */}
              {!collapsed && adminLabsTree.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ 
                    fontSize: 11, 
                    color: 'var(--text-muted)', 
                    padding: '8px 12px 4px',
                    fontWeight: 600,
                    letterSpacing: '0.5px'
                  }}>
                    QUICK ACCESS
                  </div>
                  
                  {adminLabsTree.map(termNode => (
                    <div key={termNode.term}>
                      {/* Term Level */}
                      <button
                        onClick={() => toggleTerm(termNode.term)}
                        aria-expanded={expandedTerms.has(termNode.term)}
                        aria-label={`${termNode.termDisplay} term, ${termNode.labCount} labs`}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 12px',
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-secondary)',
                          cursor: 'pointer',
                          borderRadius: 6,
                          fontSize: 13,
                          fontWeight: 500,
                          textAlign: 'left',
                        }}
                      >
                        {expandedTerms.has(termNode.term) ? (
                          <ChevronDown size={14} aria-hidden="true" />
                        ) : (
                          <ChevronRight size={14} aria-hidden="true" />
                        )}
                        <Calendar size={14} style={{ color: 'var(--accent-primary)' }} aria-hidden="true" />
                        <span style={{ flex: 1 }}>{termNode.termDisplay}</span>
                        <span style={{
                          fontSize: 10,
                          color: 'var(--text-muted)',
                          background: 'var(--bg-tertiary)',
                          padding: '2px 6px',
                          borderRadius: 4,
                        }} aria-hidden="true">
                          {termNode.labCount} labs
                        </span>
                      </button>
                      
                      {/* Courses under Term */}
                      {expandedTerms.has(termNode.term) && (
                        <div style={{ marginLeft: 12 }} role="group" aria-label={`Courses in ${termNode.termDisplay}`}>
                          {termNode.courses.map(courseNode => {
                            const termCourseKey = `${termNode.term}-${courseNode.courseCode}`;
                            return (
                              <div key={termCourseKey}>
                                {/* Course Level */}
                                <button
                                  onClick={() => toggleCourse(termCourseKey, courseNode)}
                                  aria-expanded={expandedCourses.has(termCourseKey)}
                                  aria-label={`${courseNode.courseCode}, ${courseNode.labCount} labs${courseNode.totalRunning > 0 ? `, ${courseNode.totalRunning} running` : ''}`}
                                  style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '6px 12px',
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    borderRadius: 6,
                                    fontSize: 12,
                                    textAlign: 'left',
                                  }}
                                >
                                  {expandedCourses.has(termCourseKey) ? (
                                    <FolderOpen size={13} style={{ color: 'var(--accent-warning)' }} aria-hidden="true" />
                                  ) : (
                                    <Folder size={13} style={{ color: 'var(--accent-warning)' }} aria-hidden="true" />
                                  )}
                                  <span style={{ fontWeight: 500, flex: 1 }}>{courseNode.courseCode}</span>
                                  {(courseNode.totalRunning === null || courseNode.totalRunning > 0) && (
                                    <span style={{
                                      fontSize: 9,
                                      color: 'var(--accent-success)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2,
                                    }} aria-hidden="true">
                                      <Activity size={9} />
                                      {countLabel(courseNode.totalRunning)} env
                                    </span>
                                  )}
                                  <span style={{
                                    fontSize: 10,
                                    color: 'var(--text-muted)',
                                  }} aria-hidden="true">
                                    {courseNode.labCount} labs
                                  </span>
                                </button>

                                {/* Sections under Course */}
                                {expandedCourses.has(termCourseKey) && (
                                  <div style={{ marginLeft: 16 }} role="group" aria-label={`Sections in ${courseNode.courseCode}`}>
                                    {courseNode.sections.map(sectionNode => {
                                      const sectionKey = `${termNode.term}-${courseNode.courseCode}-${sectionNode.section}`;
                                      return (
                                        <div key={sectionKey}>
                                          {/* Section Level */}
                                          <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => toggleSection(sectionKey)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection(sectionKey); } }}
                                            aria-expanded={expandedSections.has(sectionKey)}
                                            aria-label={`Section ${sectionNode.displayName}, ${sectionNode.labs.length} labs${sectionNode.totalRunning > 0 ? `, ${sectionNode.totalRunning} running` : ''}`}
                                            style={{
                                              width: '100%',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 8,
                                              padding: '5px 12px',
                                              background: 'none',
                                              border: 'none',
                                              color: 'var(--text-secondary)',
                                              cursor: 'pointer',
                                              borderRadius: 6,
                                              fontSize: 12,
                                              textAlign: 'left',
                                            }}
                                          >
                                            {expandedSections.has(sectionKey) ? (
                                              <ChevronDown size={12} aria-hidden="true" />
                                            ) : (
                                              <ChevronRight size={12} aria-hidden="true" />
                                            )}
                                            <Users size={12} style={{ color: 'var(--accent-primary)' }} aria-hidden="true" />
                                            <span style={{ fontWeight: 500, flex: 1 }}>{sectionNode.displayName}</span>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/sections?term=${termNode.term}&course=${courseNode.courseCode}&section=${sectionNode.section}`);
                                              }}
                                              title="Manage section roster"
                                              style={{
                                                background: 'none',
                                                border: 'none',
                                                color: 'var(--text-muted)',
                                                cursor: 'pointer',
                                                padding: 2,
                                                borderRadius: 4,
                                                display: 'flex',
                                                alignItems: 'center',
                                                opacity: 0.4,
                                              }}
                                              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.4'}
                                            >
                                              <Settings size={10} />
                                            </button>
                                            {(sectionNode.totalRunning === null || sectionNode.totalRunning > 0) && (
                                              <span style={{
                                                fontSize: 9,
                                                color: 'var(--accent-success)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                              }} aria-hidden="true">
                                                <Activity size={9} />
                                                {countLabel(sectionNode.totalRunning)} env
                                              </span>
                                            )}
                                            <span style={{
                                              fontSize: 10,
                                              color: 'var(--text-muted)',
                                            }} aria-hidden="true">
                                              {sectionNode.labs.length} labs
                                            </span>
                                          </div>

                                          {/* Labs under Section */}
                                          {expandedSections.has(sectionKey) && (
                                            <div style={{ marginLeft: 16 }} role="list" aria-label={`Labs in ${sectionNode.displayName}`}>
                                              {sectionNode.labs.map(lab => {
                                                const isActive = location.pathname === `/labs/${lab.id}`;
                                                const hasRunning = lab.containerStats?.running > 0;

                                                return (
                                                  <NavLink
                                                    key={lab.id}
                                                    to={`/labs/${lab.id}`}
                                                    role="listitem"
                                                    aria-label={`${labLabel(lab)}, status: ${lab.status || 'draft'}${hasRunning ? ', containers running' : ''}`}
                                                    title={lab.name}
                                                    aria-current={isActive ? 'page' : undefined}
                                                    style={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 6,
                                                      padding: '5px 10px',
                                                      borderRadius: 4,
                                                      fontSize: 12,
                                                      color: isActive
                                                        ? 'var(--text-primary)'
                                                        : 'var(--text-secondary)',
                                                      background: isActive ? 'var(--bg-tertiary)' : 'transparent',
                                                      textDecoration: 'none',
                                                    }}
                                                  >
                                                    <FlaskConical size={12} style={{
                                                      color: lab.status === 'open'
                                                        ? 'var(--accent-success)'
                                                        : lab.status === 'closed'
                                                          ? 'var(--accent-danger)'
                                                          : 'var(--accent-purple)'
                                                    }} aria-hidden="true" />
                                                    <span style={{
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      whiteSpace: 'nowrap',
                                                      flex: 1
                                                    }}>
                                                      {labLabel(lab)}
                                                    </span>
                                                    {hasRunning && (
                                                      <span
                                                        style={{
                                                          width: 6,
                                                          height: 6,
                                                          borderRadius: '50%',
                                                          background: 'var(--accent-success)',
                                                        }}
                                                        aria-hidden="true"
                                                      />
                                                    )}
                                                  </NavLink>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </nav>

        {/* Stats - Only for faculty/admin */}
        {!isStudent && !collapsed && (
          <div
            style={{
              padding: '16px',
              borderTop: '1px solid var(--border-primary)',
              flexShrink: 0,
            }}
            role="status"
            aria-label="Live container status"
            aria-live="polite"
          >
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              STUDENT ENVIRONMENTS
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <StatBadge 
                icon={Activity} 
                value={countLabel(displayStats?.running)}
                label="Running" 
                color="var(--accent-success)" 
              />
              <StatBadge 
                icon={Server} 
                value={countLabel(displayStats?.total)}
                label="Registered"
                color="var(--accent-primary)" 
              />
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
              {countLabel(displayStats?.stopped)} stopped<AdditionalEnvironmentStates stats={displayStats} prefix=" · " />
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
              Faculty pods: {countLabel(displayStats?.faculty?.total)} · Live pods: {countLabel(displayStats?.pods?.total)}
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
              {displayStats?.scope?.label || (isAdmin ? 'All labs' : 'Your labs')} · {liveConnected ? 'Live refresh' : 'Polling'}
            </div>
            <EnvironmentStatsStatus stats={displayStats} error={statsQuery.isError} loading={statsQuery.isPending} />
          </div>
        )}

        {/* User */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid var(--border-primary)',
          display: 'flex',
          flexDirection: collapsed ? 'column' : 'row',
          alignItems: 'center',
          gap: 12,
          flexShrink: 0,
        }}>
          <div style={{
            width: 32,
            height: 32,
            background: isStudent ? 'var(--accent-success)20' : 'var(--bg-tertiary)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 600,
            color: isStudent ? 'var(--accent-success)' : 'inherit',
          }}>
            {user?.name?.[0]?.toUpperCase() || user?.studentId?.[0] || 'U'}
          </div>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.name || user?.studentId || 'User'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                {user?.role}
                {user?.studentId && isStudent && ` • ${user.studentId}`}
              </div>
            </div>
          )}
          <button
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: 4,
            }}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
          </button>
          <button
            onClick={handleLogout}
            aria-label="Log out"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: 4,
            }}
            title="Logout"
          >
            <LogOut size={18} aria-hidden="true" />
          </button>
        </div>

        {/* Collapse button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          style={{
            position: 'absolute',
            left: collapsed ? 52 : 228,
            top: 72,
            width: 24,
            height: 24,
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-primary)',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-secondary)',
            transition: 'left 0.2s ease',
          }}
        >
          {collapsed ? <ChevronRight size={14} aria-hidden="true" /> : <ChevronLeft size={14} aria-hidden="true" />}
        </button>
      </aside>

      {/* Main content */}
      <main id="main-content" role="main" style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
        <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
          {children}
        </div>
      </main>

      {/* Version info footer */}
      <VersionFooter />
    </div>
  );
}

function NavItem({ to, icon: Icon, collapsed, children }) {
  return (
    <NavLink
      to={to}
      aria-label={collapsed ? children : undefined}
      title={collapsed ? children : undefined}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 8,
        color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
        background: isActive ? 'var(--bg-tertiary)' : 'transparent',
        textDecoration: 'none',
        marginBottom: 4,
        transition: 'all 0.15s ease',
      })}
    >
      <Icon size={20} aria-hidden="true" />
      {!collapsed && <span style={{ fontSize: 14 }}>{children}</span>}
    </NavLink>
  );
}

function StatBadge({ icon: Icon, value, label, color }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 10px',
        background: 'var(--bg-tertiary)',
        borderRadius: 6,
        fontSize: 12,
      }}
      aria-label={`${value} ${label}`}
    >
      <Icon size={14} style={{ color }} aria-hidden="true" />
      <span style={{ fontWeight: 600 }}>{value}</span>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
    </div>
  );
}

export default Layout;
